import React, { useCallback, useState } from "react";
import classNames from "classnames";
import { Slot } from "@radix-ui/react-slot";
import { getOppositePlacement } from '@floating-ui/utils';
import {
    offset,
    flip,
    shift,
    Placement,
    useFloating,
    useHover,
    useFocus,
    useDismiss,
    useRole,
    useInteractions
} from '@floating-ui/react';
import { InfoIcon } from "@/assets/svgs/icons";


interface TooltipProps {
    placement?: Placement;
    message: React.ReactNode;
    tooltipClassName?: string;
    offset?: number;
    disable?: boolean;
    className?: string;
    children?: React.ReactNode;
}

const Tooltip: React.FC<TooltipProps> = (props) => {
    const { children, message, placement = 'right', tooltipClassName, offset: propsOffset = 4, disable = false } = props;

    const [isOpen, setIsOpen] = useState(false);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement,
        middleware: [
            offset(propsOffset),
            flip({ fallbackPlacements: [getOppositePlacement(placement)] }),
            shift({ padding: 10 }),
        ],
    });
  
    const hover = useHover(context, {  move: false, restMs: 100, delay: { open: 0, close: 100 } });
    const focus = useFocus(context);
    const dismiss = useDismiss(context);
    const role = useRole(context);
  
    const { getReferenceProps, getFloatingProps } = useInteractions([ hover, focus, dismiss, role ]);

    const renderTrigger = useCallback(() => {
        if (children === undefined) {
            return (
                <InfoIcon className="size-3.5 text-black-400" />
            );
        } else if (!React.isValidElement(children)) {
            return <span>{children}</span>;
        } else {
            return children;
        }
    }, [children]);

    return (
      <>
        <Slot ref={refs.setReference}
              tabIndex={disable ? undefined : 0}
              {...getReferenceProps()}
        >
            {renderTrigger()}
        </Slot>
        {isOpen && !disable && (
            <div ref={refs.setFloating}
                 className={classNames(
                     "h-fit py-1.5 px-2.5 pl-3 bg-black rounded-[6px] text-white font-[400] text-[12px] whitespace-pre-wrap z-[9999]",
                     tooltipClassName
                 )}
                 style={{ ...floatingStyles }}
                 onBlur={() => setIsOpen(false)}
                 {...getFloatingProps()}
            >
                 {message}
            </div>
        )}
    </>);
};

export default Tooltip;
